import mean from 'lodash/mean'
import sum from 'lodash/sum'
import zip from 'lodash/zip'

import type { Grade, GradeData, GradeValue } from './ContentGrade.types'

export const gradeData: Record<Grade, GradeData> = {
  A: {
    color: {
      text: 'var(--color-green-800)',
      activeBackground: 'var(--color-green-200)',
      shadow: '0px 4px 8px 0px rgba(201, 247, 223, 0.90)',
      gradient:
        'linear-gradient(180deg, rgba(255, 255, 255) 68.29%, rgba(201, 247, 223, 0.30) 100%)',
    },
    copy: ['Congratulations! Your content is perfect 🙌'],
  },
  B: {
    color: {
      text: 'var(--color-green-700)',
      activeBackground: 'var(--color-green-200)',
      shadow: '0px 4px 8px 0px rgba(210, 247, 201, 0.80)',
      gradient:
        'linear-gradient(180deg, rgba(255, 255, 255) 68.29%, rgba(201, 247, 223, 0.30) 100%)',
    },
    copy: ['Congratulations! Your content is perfect 🙌'],
  },
  C: {
    color: {
      text: 'var(--color-yellow-700)',
      activeBackground: 'var(--color-yellow-200)',
      shadow: '0px 4px 8px 0px rgba(245, 237, 214, 0.80)',
      gradient:
        'linear-gradient(180deg, rgba(255, 255, 255) 68.29%, rgba(255, 235, 178, 0.30) 100%)',
    },
    copy: ['Great work! 👍 Improve a bit more to increase traffic'],
  },
  D: {
    color: {
      text: 'var(--color-orange-700)',
      activeBackground: 'var(--color-orange-200)',
      shadow: '0px 4px 8px 0px rgba(253, 195, 179, 0.80)',
      gradient:
        'linear-gradient(180deg, rgba(255, 255, 255) 68.29%, rgba(255, 196, 179, 0.30) 100%)',
    },
    copy: ['Great work! 👍 Improve a bit more to increase traffic'],
  },
  E: {
    color: {
      text: 'var(--color-orange-800)',
      activeBackground: 'var(--color-orange-200)',
      shadow: '0px 4px 8px 0px rgba(255, 177, 153, 0.80)',
      gradient:
        'linear-gradient(180deg, rgba(255, 255, 255) 68.29%, rgba(255, 196, 179, 0.30) 100%)',
    },
    copy: ['Great work! 👍 Improve a bit more to increase traffic'],
  },
}

export const weights = {
  document_length: 0.2,
  headings: 0.1,
  paragraph_density: 0.1,
  readability_grade: 0.1,
  image_count: 0.1,
  link_tally: 0.1,
  keyword_optimization: 0.3,
}

export const keyword_importance = {
  primary: 0.4,
  secondary: 0.2,
  recommended: 0.1,
  heading_presence: 0.3,
}

// ref: https://www.notion.so/pepperc/Content-Grading-for-CRR-c98e069e3d7e4b77aba1dd963de5eb4a
export function calculateContentGrade(
  current_values: GradeValue,
  target_values: GradeValue
) {
  const rawScores: Record<string, number> = {}
  const scores: Record<string, number> = {}

  for (const factor in weights) {
    if (factor === 'keyword_optimization') {
      const keywordScores = []
      const rawKeywordScores = []
      for (const keywordType in keyword_importance) {
        const currentKeywordValue = current_values[factor][keywordType]
        const targetKeywordValue = target_values[factor][keywordType]
        if (!currentKeywordValue || !targetKeywordValue) {
          throw new Error(
            `Missing keyword optimization data for ${keywordType}`
          )
        }

        for (const [current = 0, target = 0] of zip(
          currentKeywordValue,
          targetKeywordValue
        )) {
          const keywordScore =
            Math.min(current / target, 1) *
            keyword_importance[keywordType as keyof typeof keyword_importance]
          const rawKeywordScore = Math.min(current / target, 1)
          keywordScores.push(keywordScore)
          rawKeywordScores.push(rawKeywordScore)
        }

        scores[factor] = mean(keywordScores) * weights[factor]
        rawScores[factor] = mean(rawKeywordScores)
      }
    } else {
      const current = current_values[factor as keyof GradeValue] as number
      // adding 1 to target to avoid division by 0
      const target = Math.max(
        target_values[factor as keyof GradeValue] as number,
        1
      )
      scores[factor] =
        Math.min(current / target, 1) * weights[factor as keyof typeof weights]
      rawScores[factor] = Math.min(current / target, 1)
    }
  }

  const totalScore = sum(Object.values(scores)) * 100

  let grade: Grade = 'E'
  if (totalScore >= 66) grade = 'A'
  else if (totalScore >= 50) grade = 'B'
  else if (totalScore >= 33) grade = 'C'
  else if (totalScore >= 15) grade = 'D'
  else grade = 'E'

  return { totalScore, grade, scores, rawScores }
}
